// Vendor Libs
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, isEmpty, isPlainObject, noop } from 'lodash';
import classnames from 'classnames';

// Components
import Error from './error';
import FormUpdated from './form-updated';
import Loading from './loading';

// Redux Actions
import {
  formSubmitFetch,
  formSubmitReset,
  registerForm,
  resetForm,
  setFieldErrors,
  setFieldValue,
  unregisterFieldErrors,
  unregisterFieldValue,
  unregisterFormErrors,
} from '../../actions/form-data-actions';
import { refreshServicesData } from '../../actions/services-data-actions';

// Contexts
import { FormContext } from '../../contexts/form-context';

class Form extends Component {
    state = {
        successfullySubmitted: false,
    };

    componentWillMount() {
      const {name, registerForm} = this.props;
      const {name: nameContext} = this.context;
      this.formName = name || nameContext;

      if (!this.isFormRegistered()) {
        registerForm(this.formName).then(() => {
          this.setFormData();
        });
      }
    }

    componentWillReceiveProps(nextProps) {
      const {name, submitData} = nextProps;
      const prevFormData = this.getFormData(this.props);

      // Always update our internal formData reference
      this.setFormData(nextProps);
      // If the formName prop changed, update it
      if (name && this.formName !== name) {
        this.formName = name;
      }

      // When submitData goes from fetching → fetched and form is valid, mark success
      if (
        submitData &&
        prevFormData.fetching &&
        !this.formData.fetching &&
        this.formData.fetched &&
        this.isFormValid()
      ) {
        this.setState({ successfullySubmitted: true });
      }
    }

    getClass() {
      const { className } = this.props;
      return classnames('form', className);
    }

    getErrorProps() {
      const { absolutePositionedResponse, fixedPositionedResponse } = this.props;
      return {
        absolutePositioned: absolutePositionedResponse,
        className: 'form--error',
        error: get(this.formData, 'formErrors'),
        fixedPositioned: fixedPositionedResponse,
        onClear: this.formClearErrors,
        onReset: this.formSubmitReset,
        resetButtonChildren: 'reset form',
      };
    }

    getFormData(props) {
      return get(props.formData, this.formName, {});
    }

    setFieldValue = (fieldName, value, operator) => {
      const { setFieldValue } = this.props;
      return setFieldValue(this.formName, fieldName, value, operator);
    };

    setFormData(props = this.props) {
        this.formData = this.getFormData(props);
    }

    formClearErrors = () => {
      const { unregisterFormErrors } = this.props;
      unregisterFormErrors(this.formName);
    };

    formSubmitReset = () => {
      const {onReset, formSubmitReset, refreshServicesData, resetOnSubmit, updateServicesDataKey} = this.props;

      onReset(true);
      formSubmitReset(this.formName, resetOnSubmit);
      this.setState({ successfullySubmitted: false });

      if (updateServicesDataKey) {
        refreshServicesData(updateServicesDataKey);
      }
    };

    handleFormSubmit = event => {
      event && event.preventDefault && event.preventDefault();
    };

    handleReset = event => {
      const {onReset, resetForm, submitData} = this.props;
      event && event.preventDefault && event.preventDefault();
      resetForm(this.formName, !submitData, true).then(() => onReset());
    };

    handleSubmit = event => {
      event && event.preventDefault && event.preventDefault();
      const {onSubmit, formSubmitFetch, submitData, noAuthenticationRequired} = this.props;

      if (this.isFormValid()) {
        onSubmit(event);
        if (submitData) {
          formSubmitFetch(this.formName, undefined, noAuthenticationRequired);
        }
      }
    };

    isFormValid = () => !(this.hasFieldErrors() || this.hasFormErrors());

    hasFormErrors() {
      const formErrors = get(this.formData, 'formErrors');
      return formErrors && formErrors.length;
    }

    isFetching() {
      return get(this.formData, 'fetching');
    }

    isFormRegistered() {
      const { formData } = this.props;
      return isPlainObject(formData[this.formName]);
    }

    hasFieldErrors() {
      return !isEmpty(get(this.formData, 'fieldErrors'));
    }

    renderForm() {
      const { successfullySubmitted } = this.state;
      const { children, absolutePositionedResponse } = this.props;
      let content = null;

        if (!successfullySubmitted) {
            content = (
                <form name={this.formName} onSubmit={this.handleFormSubmit}>
                    {children}
                </form>
            );
        }

        if (successfullySubmitted) {
            const {editing, formLabel, successMessage, successPage} = this.props;
            if (successPage) {
                content = successPage;
            } else {
                content = (
                    <FormUpdated
                        {...{
                            absolutePositioned: absolutePositionedResponse,
                            editing,
                            formLabel,
                            formSubmitReset: this.formSubmitReset,
                            successMessage,
                        }}
                    />
                );
            }
        }

        return content;
    }

    render() {
        const {formRef, setFieldErrors, unregisterFieldErrors, unregisterFieldValue} = this.props;

        return (
            <FormContext.Provider
                value={{
                    formName: this.formName,
                    isFormValid: this.isFormValid,
                    onSubmit: this.handleSubmit,
                    onReset: this.handleReset,
                    setFieldErrors,
                    setFieldValue: this.setFieldValue,
                    unregisterFieldErrors,
                    unregisterFieldValue,
                }}
            >
                <div className={this.getClass()} ref={formRef}>
                    <Loading loading={this.isFetching()} />
                    {this.renderForm()}
                    <Error {...this.getErrorProps()} />
                </div>
            </FormContext.Provider>
        );
    }
}

Form.propTypes = {
    absolutePositionedResponse: PropTypes.bool,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    editing: PropTypes.bool,
    fixedPositionedResponse: PropTypes.bool,
    formData: PropTypes.object.isRequired,
    formLabel: PropTypes.string,
    formRef: PropTypes.func,
    formSubmitFetch: PropTypes.func.isRequired,
    formSubmitReset: PropTypes.func.isRequired,
    name: PropTypes.string,
    noAuthenticationRequired: PropTypes.bool,
    onReset: PropTypes.func,
    onSubmit: PropTypes.func,
    refreshServicesData: PropTypes.func,
    registerForm: PropTypes.func.isRequired,
    resetForm: PropTypes.func.isRequired,
    resetOnSubmit: PropTypes.bool,
    searchFetching: PropTypes.object,
    servicesDataKey: PropTypes.string,
    setFieldErrors: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    submitData: PropTypes.bool,
    successMessage: PropTypes.string,
    successPage: PropTypes.node,
    unregisterFieldErrors: PropTypes.func.isRequired,
    unregisterFieldValue: PropTypes.func.isRequired,
    unregisterFormErrors: PropTypes.func.isRequired,
    updateServicesDataKey: PropTypes.string,
};

Form.defaultProps = {
    onSubmit: noop,
    onReset: noop,
};

const mapStateToProps = state => ({
    formData: state.formData,
});

const mapDispatchToProps = {
    formSubmitFetch,
    formSubmitReset,
    refreshServicesData,
    registerForm,
    resetForm,
    setFieldErrors,
    setFieldValue,
    unregisterFieldErrors,
    unregisterFieldValue,
    unregisterFormErrors,
};

export default connect(mapStateToProps, mapDispatchToProps)(Form);
